import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { cabinetInfo } from '../../mock';

const blocks = [
  {
    id: 'implants',
    eyebrow: 'Implantologie',
    title: 'Une racine',
    italic: 'naturelle',
    body:
      "Solutions permanentes pour remplacer une ou plusieurs dents avec planification numérique 3D et résultats durables.",
    image: '/image/service-implants.webp',
    cta: { label: 'Prendre rendez-vous', external: true },
  },
  {
    id: 'aligners',
    eyebrow: 'Orthodontie invisible',
    title: 'Sourire',
    italic: 'discret',
    body:
      "Gouttières transparentes fabriquées au cabinet, suivi personnalisé. Une alternative confortable aux bagues classiques.",
    image: '/image/service-aligners.webp',
    cta: { label: 'Prendre rendez-vous', external: true },
  },
  {
    id: 'techno',
    eyebrow: 'Plateau technique',
    title: 'Précision',
    italic: 'instrumentale',
    body:
      "Radiographie 3D, empreintes numériques, laser dentaire et stérilisation avancée — un cabinet ultra-moderne au service du soin.",
    image: '/image/techno-3dscan.webp',
    cta: { label: 'Découvrir', scrollTo: 'technologies' },
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-100px' },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

export default function SpecialitesV2({ scrollToSection }) {
  return (
    <section id="specialites" className="bg-muted/40 py-24 lg:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div {...fadeUp} className="mb-20 lg:mb-28">
          <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-foreground/55">
            02 — Nos spécialités
          </span>
          <h2 className="mt-4 font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tight text-foreground max-w-4xl">
            Trois spécialités, un même <span className="italic">parti pris</span>.
          </h2>
        </motion.div>

        <div className="space-y-24 lg:space-y-32">
          {blocks.map((b, idx) => {
            const reverse = idx % 2 === 1;
            return (
              <motion.article
                key={b.id}
                {...fadeUp}
                className="grid grid-cols-1 lg:grid-cols-12 gap-y-8 lg:gap-x-12 items-center"
              >
                <div className={`lg:col-span-7 ${reverse ? 'lg:order-2' : ''}`}>
                  <div className="relative aspect-[4/3] overflow-hidden rounded-md ring-1 ring-foreground/10 group">
                    <img
                      src={b.image}
                      alt={`${b.title} ${b.italic}`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                    />
                  </div>
                </div>

                <div className={`lg:col-span-5 ${reverse ? 'lg:order-1' : ''}`}>
                  <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-foreground/55">
                    {b.eyebrow}
                  </span>
                  <h3 className="mt-4 font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tight text-foreground">
                    {b.title}
                    <br />
                    <span className="italic text-foreground/85">{b.italic}</span>.
                  </h3>
                  <p className="mt-6 max-w-md text-base text-foreground/65 leading-relaxed">
                    {b.body}
                  </p>
                  {b.cta.external ? (
                    <a
                      href={cabinetInfo.doctolib}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-7 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-foreground text-background text-xs font-semibold uppercase tracking-[0.2em] hover:bg-amber-500 hover:text-foreground hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 transition-all"
                    >
                      {b.cta.label}
                      <ArrowRight size={14} aria-hidden="true" />
                    </a>
                  ) : (
                    <button
                      type="button"
                      onClick={() => scrollToSection(b.cta.scrollTo)}
                      className="mt-7 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-transparent text-foreground text-xs font-semibold uppercase tracking-[0.2em] ring-1 ring-foreground/20 hover:bg-foreground/5 hover:ring-foreground/50 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 transition-all"
                    >
                      {b.cta.label}
                      <ArrowRight size={14} aria-hidden="true" />
                    </button>
                  )}
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
